import React, { useState, useEffect, useRef } from 'react';
import { 
  FaUsers, 
  FaUserPlus, 
  FaDownload, 
  FaChevronLeft, 
  FaChevronRight, 
  FaTrash, 
  FaUserEdit,
  FaSearch,
  FaFilter,
  FaTimes,
  FaExclamationTriangle
} from 'react-icons/fa';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../../config/firebaseConfig';
import * as XLSX from 'xlsx';
import styles from './EmployeeInsurancePage.module.css';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import TableFilter from '../../components/TableFilter';
import AddInsuranceModal from './AddInsuranceModal';
import EditInsuranceModal from './EditInsuranceModal';
import { useTheme } from '../../contexts/ThemeContext';
import './EmployeeInsuranceDarkMode.css'; // Import dark mode styles

const truncateText = (text, maxLength = 15) => {
  if (
    text === null || 
    text === undefined || 
    text === '' || 
    Number.isNaN(text) ||
    (typeof text === 'string' && 
      ['nan', 'nat', 'n/a', 'null', 'undefined'].includes(text.toLowerCase()))
  ) {
    return '-';
  }
  const textStr = String(text);
  return textStr.length > maxLength 
    ? `${textStr.substring(0, maxLength)}...` 
    : textStr;
};

const EmployeeInsurancePage = () => {
  const { isDarkMode } = useTheme(); // Use the theme context
  const [insurances, setInsurances] = useState([]);
  const [selectedInsurance, setSelectedInsurance] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [filteredInsurances, setFilteredInsurances] = useState([]);
  const [filters, setFilters] = useState({});
  const [showLeftScroll, setShowLeftScroll] = useState(false);
  const [showRightScroll, setShowRightScroll] = useState(false);
  const tableContainerRef = useRef(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showExpiringTariffs, setShowExpiringTariffs] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const filtersRef = useRef(null);

  const fetchInsurances = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'Insurances'));
      const insuranceData = querySnapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() }));
      setInsurances(insuranceData);
    } catch (error) {
      console.error('Error fetching insurances:', error);
      toast.error('Sigorta bilgileri alınırken bir hata oluştu');
    }
  };

  const fetchEmployees = async () => {
    try {
      const insuranceSnapshot = await getDocs(collection(db, 'Insurances'));
      const insurances = insuranceSnapshot.docs.map(doc => doc.data());

      const employeeSnapshot = await getDocs(collection(db, 'Employees'));
      const employeeData = employeeSnapshot.docs
        .map((doc) => ({
          id: doc.id,
          eName: doc.data().eName,
          eSurname: doc.data().eSurname,
          Active: doc.data().Active
        }))
        .filter(employee => {
          const hasInsurance = insurances.some(
            insurance => insurance.employeeId === employee.id
          );
          return !hasInsurance;
        });

      setEmployees(employeeData);
    } catch (error) {
      console.error('Error fetching employees:', error);
      toast.error('Çalışan bilgileri alınırken bir hata oluştu');
    }
  };

  useEffect(() => {
    fetchInsurances();
    fetchEmployees();

    // Add click event listener to close filters dropdown when clicking outside
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleClickOutside = (event) => {
    if (filtersRef.current && !filtersRef.current.contains(event.target)) {
      setShowFilters(false);
    }
  };

  useEffect(() => {
    const checkExpiringInsurances = () => {
      const today = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      const expiring = insurances.filter(insurance => {
        const checkDate = (date) => {
          if (!date) return false;
          const expiryDate = new Date(date);
          return !isNaN(expiryDate) && expiryDate <= oneMonthFromNow && expiryDate >= today;
        };

        return checkDate(insurance.accidentInsurance?.endDate) || 
               checkDate(insurance.complementaryHealth?.endDate) ||
               checkDate(insurance.malpractice?.endDate);
      });

      if (expiring.length > 0) {
        toast.warn(`${expiring.length} sigortanın bitiş tarihi 1 ay içinde!`);
      }
    };

    if (insurances.length > 0) {
      checkExpiringInsurances();
    }
  }, [insurances]);

  useEffect(() => {
    let filtered = insurances;
    
    // Apply search term first (searches employee name)
    if (searchTerm) {
      const lowercasedSearch = searchTerm.toLowerCase();
      filtered = filtered.filter(
        insurance => 
          (insurance.employeeName && insurance.employeeName.toString().toLowerCase().includes(lowercasedSearch))
      );
    }

    // Apply column filters
    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        if (key === 'hasBES') {
          filtered = filtered.filter(insurance =>
            String(insurance[key]) === filters[key]
          );
        } else if (key.includes('.')) {
          const [parent, child] = key.split('.');
          filtered = filtered.filter(insurance =>
            insurance[parent]?.[child]?.toLowerCase().includes(filters[key].toLowerCase())
          );
        } else {
          filtered = filtered.filter(insurance =>
            String(insurance[key] || '')
              .toLowerCase()
              .includes(filters[key].toLowerCase())
          );
        }
      }
    });

    if (filtered.length === 1) {
      filtered.push({
        ...filtered[0],
        id: 'empty-row',
        isEmpty: true
      });
    }

    setFilteredInsurances(filtered);
  }, [insurances, filters, searchTerm]);

  const handleFilter = (column, value) => {
    const newFilters = { ...filters, [column]: value };
    setFilters(newFilters);
  };

  const clearFilters = () => {
    setFilters({});
    setSearchTerm('');
  };

  const activeCount = insurances.length; // Assuming all insurances are active for simplicity
  const inactiveCount = 0; // No inactive insurances for simplicity

  const handleRowClick = (insurance) => {
    setSelectedInsurance(insurance);
  };

  const handleShowAddModal = () => {
    setSelectedInsurance(null);
    setShowAddModal(true);
  };

  const handleEditInsurance = () => {
    if (selectedInsurance) {
      setShowEditModal(true);
    } else {
      toast.info('Lütfen düzenlemek için bir sigorta seçin');
    }
  };

  const handleDeleteInsurance = async () => {
    if (!selectedInsurance) {
      toast.info('Lütfen silmek için bir sigorta seçin');
      return;
    }

    if (window.confirm('Bu sigortayı silmek istediğinize emin misiniz?')) {
      try {
        await deleteDoc(doc(db, 'Insurances', selectedInsurance.id));
        toast.success('Sigorta başarıyla silindi');
        setSelectedInsurance(null);
        fetchInsurances();
      } catch (error) {
        console.error('Error deleting insurance:', error);
        toast.error('Sigorta silinirken bir hata oluştu');
      }
    }
  };

  const downloadExcel = () => {
    const dataToExport = insurances.map((insurance) => ({
      'Çalışan Adı': insurance.employeeName,
      'BES Durumu': insurance.hasBES ? 'Var' : 'Yok',
      'Ferdi Kaza Başlangıç': insurance.accidentInsurance?.startDate,
      'Ferdi Kaza Bitiş': insurance.accidentInsurance?.endDate,
      'Tamamlayıcı Sağlık Başlangıç': insurance.complementaryHealth?.startDate,
      'Tamamlayıcı Sağlık Bitiş': insurance.complementaryHealth?.endDate,
      'Malpraktis Başlangıç': insurance.malpractice?.startDate,
      'Malpraktis Bitiş': insurance.malpractice?.endDate,
      'Maliyet': insurance.cost,
    }));

    const worksheet = XLSX.utils.json_to_sheet(dataToExport);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Insurances');
    XLSX.writeFile(workbook, 'SigortaListesi.xlsx');
    
    toast.success('Excel dosyası başarıyla indirildi');
  };

  const handleScroll = () => {
    if (tableContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = tableContainerRef.current;
      setShowLeftScroll(scrollLeft > 0);
      setShowRightScroll(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      handleScroll();
      
      // Prevent wheel events from propagating beyond the table container
      const preventWheelPropagation = (e) => {
        // Check if the table actually needs scrolling
        const { scrollHeight, clientHeight, scrollWidth, clientWidth } = container;
        const hasVerticalScroll = scrollHeight > clientHeight;
        const hasHorizontalScroll = scrollWidth > clientWidth;
        
        // Only handle the event if there's something to scroll
        if (hasVerticalScroll || hasHorizontalScroll) {
          // Always prevent the wheel event from affecting the page
          e.stopPropagation();
          e.preventDefault();
          
          // Manually handle the scroll within the table container
          const scrollAmount = e.deltaY || e.deltaX;
          if (e.shiftKey || (!hasVerticalScroll && hasHorizontalScroll)) {
            // Horizontal scroll if shift is pressed or only horizontal scrolling is available
            container.scrollLeft += scrollAmount;
          } else {
            // Vertical scroll
            container.scrollTop += scrollAmount;
          }
        }
      };
      
      container.addEventListener('scroll', handleScroll);
      container.addEventListener('wheel', preventWheelPropagation, { passive: false });
      
      return () => {
        container.removeEventListener('scroll', handleScroll);
        container.removeEventListener('wheel', preventWheelPropagation);
      };
    }
  }, [insurances, filteredInsurances]);

  const scrollTable = (direction) => {
    if (tableContainerRef.current) {
      const scrollAmount = 300;
      const newScrollLeft = tableContainerRef.current.scrollLeft + 
        (direction === 'right' ? scrollAmount : -scrollAmount);
      
      tableContainerRef.current.scrollTo({
        left: newScrollLeft,
        behavior: 'smooth'
      });
    }
  };

  const handleListExpiringTariffs = () => {
    setShowExpiringTariffs(prev => !prev);
  };

  const filteredData = showExpiringTariffs
    ? insurances.filter(insurance => {
      const today = new Date();
      const oneMonthFromNow = new Date();
      oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);

      const checkDate = (date) => {
        if (!date) return false;
        const expiryDate = new Date(date);
        return !isNaN(expiryDate) && expiryDate <= oneMonthFromNow && expiryDate >= today;
      };

      return checkDate(insurance.accidentInsurance?.endDate) || 
             checkDate(insurance.complementaryHealth?.endDate) ||
             checkDate(insurance.malpractice?.endDate);
    })
    : filteredInsurances;

  // Apply dark mode to document body
  useEffect(() => {
    if (isDarkMode) {
      document.body.classList.add('dark-mode');
    } else {
      document.body.classList.remove('dark-mode');
    }
    
    // Cleanup
    return () => {
      document.body.classList.remove('dark-mode');
    };
  }, [isDarkMode]);

  return (
    <div className={`${styles.pageContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className={`${styles.container} ${isDarkMode ? 'dark-mode' : ''}`}>
        <header className={`${styles.header} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.headerContent}>
            <h1 className={styles.title}>
              <FaUsers className={styles.headerIcon} />
              Sigorta Yönetimi
            </h1>
            <div className={styles.headerButtons}>
              <button className={styles.addInsuranceButton} onClick={handleShowAddModal}>
                <FaUserPlus className={styles.buttonIcon} /> Sigorta Ekle
              </button>
              <button className={styles.downloadButton} onClick={downloadExcel}>
                <FaDownload className={styles.buttonIcon} /> Tabloyu İndir
              </button>
            </div>
          </div>
        </header>

        <div className={`${styles.dashboard} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={styles.statsCards}>
            <div className={styles.card}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{insurances.length}</p>
                  <h3 className={styles.cardTitle}>Toplam Sigorta</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.activeCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{activeCount}</p>
                  <h3 className={styles.cardTitle}>Aktif Sigorta</h3>
                </div>
              </div>
            </div>
            
            <div className={`${styles.card} ${styles.inactiveCard}`}>
              <div className={styles.cardContent}>
                <div className={styles.cardIcon}>
                  <FaUsers />
                </div>
                <div className={styles.cardInfo}>
                  <p className={styles.cardValue}>{inactiveCount}</p>
                  <h3 className={styles.cardTitle}>İnaktif Sigorta</h3>
                </div>
              </div>
            </div>
          </div>

          <div className={styles.searchFilterContainer}>
            <div className={styles.searchBar}>
              <FaSearch className={styles.searchIcon} />
              <input
                type="text"
                placeholder="Çalışan adı ile ara..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className={styles.searchInput}
              />
              {searchTerm && (
                <button 
                  className={styles.clearSearch} 
                  onClick={() => setSearchTerm('')}
                >
                  <FaTimes />
                </button>
              )}
            </div>
            
            <div className={styles.filterContainer} ref={filtersRef}>
              <button 
                className={styles.filterButton}
                onClick={() => setShowFilters(!showFilters)}
              >
                <FaFilter className={styles.filterIcon} />
                <span>Filtreler</span>
                {Object.keys(filters).length > 0 && (
                  <span className={styles.filterBadge}>
                    {Object.keys(filters).length}
                  </span>
                )}
              </button>
              
              {showFilters && (
                <div className={styles.filterDropdown}>
                  <div className={styles.filterHeader}>
                    <h3>Filtreler</h3>
                    <button 
                      className={styles.clearFiltersBtn}
                      onClick={clearFilters}
                    >
                      Tümünü Temizle
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {selectedInsurance && !selectedInsurance.isEmpty && (
          <div className={`${styles.actions} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={styles.selectedEmployeeInfo}>
              <h3 className={styles.selectedName}>
                {selectedInsurance.employeeName}
              </h3>
              <span className={`${styles.statusBadge} ${
                selectedInsurance.hasBES
                  ? styles.statusActive
                  : styles.statusInactive
              }`}>
                BES: {selectedInsurance.hasBES ? 'Var' : 'Yok'}
              </span>
            </div>
            
            <div className={styles.actionButtons}>
              <button className={`${styles.actionButton} ${styles.editInsuranceButton}`} onClick={handleEditInsurance}>
                <FaUserEdit className={styles.actionIcon} />
                <span>Sigortayı Düzenle</span>
              </button>
              
              <button className={`${styles.actionButton} ${styles.deleteInsuranceButton}`} onClick={handleDeleteInsurance}>
                <FaTrash className={styles.actionIcon} />
                <span>Sigorta Sil</span>
              </button>
            </div>
          </div>
        )}

        <div className={styles.searchFilterContainer}>
          <label className={styles.label}>
            <input
              type="checkbox"
              checked={showExpiringTariffs}
              onChange={handleListExpiringTariffs}
              className={styles.checkbox}
            />
            <span style={{ marginLeft: '0.5rem', display: 'inline-flex', alignItems: 'center' }}>
              <FaExclamationTriangle style={{ color: '#F59E0B', marginRight: '0.5rem' }} />
              Sigorta Bitiş Tarihi Yaklaşanları Listele
            </span>
          </label>
        </div>

        <section className={`${styles.tableSection} ${isDarkMode ? 'dark-mode' : ''}`}>
          <div className={`${styles.tableWrapper} ${isDarkMode ? 'dark-mode' : ''}`}>
            <div className={`${styles.tableScrollContainer} ${isDarkMode ? 'dark-mode' : ''}`}>
              {showLeftScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollLeft}`}
                  onClick={() => scrollTable('left')}
                >
                  <FaChevronLeft />
                </button>
              )}
              {showRightScroll && (
                <button
                  className={`${styles.scrollButton} ${styles.scrollRight}`}
                  onClick={() => scrollTable('right')}
                >
                  <FaChevronRight />
                </button>
              )}
              <div
                ref={tableContainerRef}
                className={`${styles.tableContainer} ${isDarkMode ? 'dark-mode' : ''}`}
                onScroll={handleScroll}
                onWheel={(e) => e.stopPropagation()}
              >
                <table className={`${styles.table} ${isDarkMode ? 'dark-mode' : ''}`}>
                  <thead>
                    <tr>
                      <th>
                        Çalışan Adı
                        <TableFilter column="employeeName" type="text" onFilter={handleFilter} />
                      </th>
                      <th>
                        BES Durumu
                        <TableFilter
                          column="hasBES"
                          type="select"
                          options={[
                            { value: "", label: "Tümü" },
                            { value: "true", label: "Var" },
                            { value: "false", label: "Yok" }
                          ]}
                          onFilter={handleFilter}
                        />
                      </th>
                      <th>
                        Ferdi Kaza Başlangıç
                        <TableFilter column="accidentInsurance.startDate" type="date" onFilter={handleFilter} />
                      </th>
                      <th>
                        Ferdi Kaza Bitiş
                        <TableFilter column="accidentInsurance.endDate" type="date" onFilter={handleFilter} />
                      </th>
                      <th>
                        Tamamlayıcı Sağlık Başlangıç
                        <TableFilter column="complementaryHealth.startDate" type="date" onFilter={handleFilter} />
                      </th>
                      <th>
                        Tamamlayıcı Sağlık Bitiş
                        <TableFilter column="complementaryHealth.endDate" type="date" onFilter={handleFilter} />
                      </th>
                      <th>
                        Malpraktis Başlangıç
                        <TableFilter column="malpractice.startDate" type="date" onFilter={handleFilter} />
                      </th>
                      <th>
                        Malpraktis Bitiş
                        <TableFilter column="malpractice.endDate" type="date" onFilter={handleFilter} />
                      </th>
                      <th>
                        Maliyet
                        <TableFilter column="cost" type="number" onFilter={handleFilter} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredData.length > 0 ? (
                      filteredData.map((insurance, index) => (
                        !insurance.isEmpty && (
                          <tr 
                            key={insurance.id} 
                            onClick={() => handleRowClick(insurance)}
                            className={selectedInsurance && selectedInsurance.id === insurance.id ? styles.selectedRow : ''}
                          >
                            <td title={insurance.employeeName}>{truncateText(insurance.employeeName)}</td>
                            <td>
                              <span className={`${styles.statusBadge} ${
                                insurance.hasBES
                                  ? styles.statusActive
                                  : styles.statusInactive
                              }`}>
                                {insurance.hasBES ? 'Var' : 'Yok'}
                              </span>
                            </td>
                            <td title={insurance.accidentInsurance?.startDate}>{truncateText(insurance.accidentInsurance?.startDate)}</td>
                            <td title={insurance.accidentInsurance?.endDate}>{truncateText(insurance.accidentInsurance?.endDate)}</td>
                            <td title={insurance.complementaryHealth?.startDate}>{truncateText(insurance.complementaryHealth?.startDate)}</td>
                            <td title={insurance.complementaryHealth?.endDate}>{truncateText(insurance.complementaryHealth?.endDate)}</td>
                            <td title={insurance.malpractice?.startDate}>{truncateText(insurance.malpractice?.startDate)}</td>
                            <td title={insurance.malpractice?.endDate}>{truncateText(insurance.malpractice?.endDate)}</td>
                            <td title={`${insurance.cost} ₺`}>{truncateText(`${insurance.cost} ₺`)}</td>
                          </tr>
                        )
                      ))
                    ) : (
                      <tr>
                        <td colSpan="9" className={styles.noDataCell}>
                          <div className={styles.noData}>
                            <FaUsers className={styles.noDataIcon} />
                            <p>Sigorta kaydı bulunamadı</p>
                            {(Object.keys(filters).length > 0 || searchTerm || showExpiringTariffs) && (
                              <button className={styles.clearFiltersButton} onClick={() => {
                                clearFilters();
                                setShowExpiringTariffs(false);
                              }}>
                                Filtreleri Temizle
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </section>

        <AddInsuranceModal
          show={showAddModal}
          handleClose={() => setShowAddModal(false)}
          onInsuranceAdded={fetchInsurances}
          selectedInsurance={selectedInsurance}
          employees={employees}
        />

        {showEditModal && selectedInsurance && !selectedInsurance.isEmpty && (
          <EditInsuranceModal
            show={showEditModal}
            handleClose={() => setShowEditModal(false)}
            selectedInsurance={selectedInsurance}
            onInsuranceUpdated={() => {
              fetchInsurances();
              setShowEditModal(false);
              setSelectedInsurance(null);
            }}
          />
        )}

        <ToastContainer
          position="top-right"
          autoClose={3000}
          hideProgressBar={false}
          newestOnTop
          closeOnClick
          rtl={false}
          pauseOnFocusLoss
          draggable
          pauseOnHover
          theme={isDarkMode ? "dark" : "light"}
        />
      </div>
    </div>
  );
};

export default EmployeeInsurancePage;